import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Paper, Typography } from '@/shared/ui/material';
import { Lock as LockIcon } from '@/shared/ui/icons';
import { usePageTranslation, useRoleDisplayName } from '@/shared/hooks';
import type { Permission, UserRole } from '../../types/auth';
import { findNavItemTextKey } from './navigationMenu';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requirePermission?: Permission;
    requireRole?: UserRole | UserRole[];
}

export default function ProtectedRoute({
    children,
    requirePermission,
    requireRole,
}: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, user, hasPermission, hasRole } = useAuth();
    const location = useLocation();
    const { t: translateMenu } = usePageTranslation({ filePath: 'shared/components/Layout' });
    const getRoleDisplayName = useRoleDisplayName();
    const featureTextKey = findNavItemTextKey(location.pathname);

    // Loading state
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // 未登入，重定向到登入頁
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 檢查權限
    if (requirePermission && !hasPermission(requirePermission)) {
        return (
            <Box
                sxProp={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                }}
            >
                <Paper sxProp={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
                    <LockIcon sxProp={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        無權限存取
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        您的帳號（{user?.name}）沒有權限存取此功能
                    </Typography>
                    {featureTextKey && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            功能：{translateMenu(featureTextKey)}
                        </Typography>
                    )}
                </Paper>
            </Box>
        );
    }

    // 檢查角色
    if (requireRole && !hasRole(requireRole)) {
        return (
            <Box
                sxProp={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                }}
            >
                <Paper sxProp={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
                    <LockIcon sxProp={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        無權限存取
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        您的帳號（{user?.name}）沒有權限存取此功能
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        角色：{user ? getRoleDisplayName(user.role) : ''}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return <>{children}</>;
}
